import { useCallback, useMemo, useReducer } from 'react';
import { FormattedMessage } from 'react-intl';
import styled, { css } from 'styled-components';

import { Button } from '#src/atoms/buttons/Button/index.tsx';
import { Vertical } from '#src/atoms/layout/flex/index.tsx';
import { BodyM, LabelS } from '#src/atoms/typography/index.tsx';
import { useSendDeviceConfirmation } from '#src/components/devices/DeviceNeedsConfirming/SendDeviceConfirmation/useSendDeviceConfirmation.tsx';
import { Dialog } from '#src/components/dialog/index.tsx';
import { Input } from '#src/components/form/Form/IntlTelInput/index.tsx';
import { EnterVerificationCode } from '#src/components/settings/PhoneNumberVerification/EnterVerificationCode/index.tsx';
import { Header } from '#src/components/settings/PhoneNumberVerification/Header/index.tsx';
import { PhoneNumberVerificationButton } from '#src/components/settings/PhoneNumberVerification/PhoneNumberVerificationButton/index.tsx';
import { SendVerificationCode } from '#src/components/settings/PhoneNumberVerification/SendVerificationCode/index.tsx';
import { messages } from '#src/components/settings/PhoneNumberVerification/i18n.ts';
import {
  canProceedToVerificationCodeInput,
  useVerifyPhoneNumber,
} from '#src/components/user/VerifyPhoneNumber/useVerifyPhoneNumber.ts';
import { useCurrentUserContext } from '#src/contexts/currentUser/index.ts';
import { useWalletContext } from '#src/contexts/wallet/index.ts';
import { userAttributes } from '#src/lib/glossary.ts';

import {
  close,
  completed,
  enterVerificationCode,
  openDialog,
} from './actions.ts';
import { init, reducer } from './reducer.ts';
import type {
  InputPhoneNumberState,
  InputVerificationCodeState,
  State,
} from './types.ts';

const HeaderWrapper = styled.div`
  padding: var(--triple-unit) var(--triple-unit) 0 var(--triple-unit);
`;
const Body = styled.div`
  padding: var(--unit);
`;
const Wrapper = styled.div<{ disabled: boolean }>`
  ${props =>
    props.disabled &&
    css`
      pointer-events: none;
      opacity: 0.5;
    `}
`;

const StyledButton = styled(Button)`
  align-self: flex-start;
`;

type ChangePhoneNumberProps = {
  onClick: () => void;
};

const verificationPhoneSectionReducer = reducer();

const ChangePhoneNumber = ({ onClick }: ChangePhoneNumberProps) => {
  const { currentUser } = useCurrentUserContext();

  if (!currentUser) return null;

  if (currentUser.phoneNumber) {
    return (
      <>
        <BodyM>
          <FormattedMessage {...userAttributes.phoneNumber} />
        </BodyM>
        <Input defaultPhone={currentUser.phoneNumber} noBorder disabled />
        <PhoneNumberVerificationButton
          message={messages.changePhoneNumber}
          onClick={onClick}
        />
      </>
    );
  }

  return null;
};

const isInputPhoneNumberStatus = (
  status: State
): status is InputPhoneNumberState => status.stage === 'input_number';

const isEnterVerificationCodeStatus = (
  status: State
): status is InputVerificationCodeState =>
  status.stage === 'enter_verification_code';

export const PhoneNumberVerificationSection = () => {
  const { sendDeviceConfirmation, sending } = useSendDeviceConfirmation();
  const {
    currentUser,
    emailConfirmationNeeded,
    currentDeviceConfirmationNeeded,
  } = useCurrentUserContext();
  const [state, dispatch] = useReducer(
    verificationPhoneSectionReducer,
    {
      currentUser: currentUser!,
    },
    init
  );

  const switchToInputNumber = useCallback(() => {
    dispatch(openDialog());
  }, []);
  const verifyPhoneNumber = useVerifyPhoneNumber();
  const { checkUserPhoneNumberVerificationCodeWithRecovery } =
    useWalletContext();

  const sendVerificationCode = useCallback(
    async (phoneNumber: any) =>
      verifyPhoneNumber(phoneNumber).then(async res => {
        if (canProceedToVerificationCodeInput(res)) {
          dispatch(enterVerificationCode(phoneNumber));
        }
        return Promise.resolve(res || []);
      }),
    [verifyPhoneNumber]
  );

  const submitVerificationCode = useCallback(
    async (verificationCode: any) =>
      checkUserPhoneNumberVerificationCodeWithRecovery(verificationCode).then(
        async res => {
          if (!res || res.length === 0) {
            dispatch(completed());
          }
          return res || [];
        }
      ),
    [checkUserPhoneNumberVerificationCodeWithRecovery]
  );

  const goBackCb = useMemo(() => {
    const { goBack } = state;
    if (goBack) {
      return () => {
        goBack(dispatch);
      };
    }
    return undefined;
  }, [state]);

  const closeCb = useMemo(() => {
    const { close: dialogClose } = state;
    if (dialogClose) {
      return () => {
        dialogClose(dispatch);
      };
    }
    return undefined;
  }, [state]);

  return (
    <Vertical>
      {!!emailConfirmationNeeded && (
        <LabelS color="var(--c-red-200)">
          <FormattedMessage defaultMessage="Please confirm your email address prior to adding a phone number." />
        </LabelS>
      )}
      {!!currentDeviceConfirmationNeeded && (
        <Vertical>
          <LabelS color="var(--c-red-200)">
            <FormattedMessage defaultMessage="Please confirm your device prior to adding a phone number." />
          </LabelS>
          <StyledButton
            size="compact"
            onClick={sendDeviceConfirmation}
            loading={sending}
          >
            <FormattedMessage defaultMessage="Resend device confirmation email" />
          </StyledButton>
        </Vertical>
      )}
      <Wrapper
        disabled={emailConfirmationNeeded || currentDeviceConfirmationNeeded}
      >
        <ChangePhoneNumber onClick={switchToInputNumber} />
        <Dialog
          open={state.stage !== 'status'}
          maxWidth="xs"
          fullWidth
          hideHeader
        >
          <Vertical gap={0}>
            <HeaderWrapper>
              <Header onBack={goBackCb} onClose={closeCb} />
            </HeaderWrapper>
            <Body>
              {isInputPhoneNumberStatus(state) && (
                <SendVerificationCode
                  sendVerificationCode={sendVerificationCode}
                />
              )}
              {isEnterVerificationCodeStatus(state) && (
                <EnterVerificationCode
                  resendVerificationCode={async () =>
                    verifyPhoneNumber(state.unverifiedPhoneNumber).then(
                      res => res || []
                    )
                  }
                  phoneNumber={state.unverifiedPhoneNumber}
                  submitVerificationCode={submitVerificationCode}
                  onCancel={close}
                />
              )}
            </Body>
          </Vertical>
        </Dialog>
      </Wrapper>
    </Vertical>
  );
};
